import { z } from 'zod';
import { ProgressionLevel } from '@/lib/progressions';

// Base types
export interface User {
  id: string;
  username: string;
  createdAt: number;
  lastSeen: number;
}

export interface ExerciseOption {
  id: string;
  name: string;
  icon: string;
  unit: 'reps' | 'meters' | 'kilograms' | 'minutes';
}

export interface CustomExercise {
  name: string;
  icon: string;
  unit: 'reps' | 'meters' | 'kilograms' | 'minutes';
}

export interface Challenge {
  id: string;
  name: string;
  exerciseType: string;
  customExercise?: CustomExercise;
  target: number;
  startDate: string;
  endDate: string;
  createdBy: string;
  participants: string[];
  createdAt: number;
  updatedAt: number;
  restDaysAllowed?: number;
}

export interface ExerciseLog {
  id: string;
  userId: string;
  challengeId: string;
  amount: number;
  date: string;
  timestamp: number;
  exerciseType?: string; // Optional for backwards compatibility, will be required after migration
  exerciseUnit?: ExerciseUnit; // Optional for backwards compatibility
  progression?: ProgressionLevel; // Optional for backwards compatibility, defaults to MASTER
}

export interface RestDay {
  id: string;
  userId: string;
  challengeId: string;
  date: string;
  timestamp: number;
}

export interface Participant {
  userId: string;
  username: string;
  joinedAt: number;
  totalAmount: number;
  dailyLog: Record<string, number>;
  sets: Record<string, number[]>;
  restDays: string[]; // Array of dates marked as rest days
  progressionStats?: Record<ProgressionLevel, number>; // Track reps per progression level
}


// API Response types
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  timestamp: number;
}

export interface AuthResponse {
  user: User;
  token: string;
}



// Zod validation schemas
export const UserSchema = z.object({
  id: z.string().uuid(),
  username: z.string().min(2).max(50),
  createdAt: z.number(),
  lastSeen: z.number(),
});

export const ExerciseLogSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  challengeId: z.string().min(6).max(36), // Support both short IDs and UUIDs
  amount: z.number().positive(),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  timestamp: z.number(),
  exerciseType: z.string().optional(), // Optional for backwards compatibility
  exerciseUnit: z.enum(['reps', 'meters', 'kilograms', 'minutes']).optional(), // Optional for backwards compatibility
  progression: z
    .enum(['BEGINNER', 'NOVICE', 'ATHLETE', 'MASTER', 'ELITE', 'TERMINATOR'])
    .optional(), // Optional for backwards compatibility
});

export const RestDaySchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  challengeId: z.string().min(6).max(36),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  timestamp: z.number(),
});

export const ChallengeSchema = z.object({
  id: z.string().min(6).max(36), // Support both short IDs and UUIDs
  name: z.string().min(1).max(100),
  exerciseType: z.string(),
  customExercise: z
    .object({
      name: z.string().min(1).max(50),
      icon: z.string().min(1).max(2),
      unit: z.enum(['reps', 'meters', 'kilograms', 'minutes']),
    })
    .optional(),
  target: z.number().positive(),
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  createdBy: z.string().uuid(),
  participants: z.array(z.string().uuid()),
  createdAt: z.number(),
  updatedAt: z.number(),
  restDaysAllowed: z.number().min(0).max(30).optional(),
});

// Utility types
export type ExerciseUnit = 'reps' | 'meters' | 'kilograms' | 'minutes';

export type ViewType =
  | 'home'
  | 'create'
  | 'join'
  | 'share'
  | 'challenge'
  | 'gympass'
  | 'passlogin'
  | 'stats';

export interface AppState {
  currentView: ViewType;
  currentChallenge: Challenge | null;
  user: User | null;
  isAuthenticated: boolean;
  isOffline: boolean;
  isLoading: boolean;
  error: string | null;
}


// User Stats types
export interface ExerciseStat {
  exerciseType: string;
  exerciseName: string;
  icon: string;
  unit: ExerciseUnit;

  // Totals
  totalAmount: number;
  totalSets: number;
  daysActive: number;

  // Records
  bestSet: { amount: number; date: string };
  bestDay: { amount: number; date: string };
  averagePerSet: number;

  // Recent Performance
  last7Days: number;
  last30Days: number;

  // Progression Stats
  progressionStats?: Record<string, number>;
}

export interface CompletedChallenge {
  challengeId: string;
  challengeName: string;
  exerciseType: string;
  exerciseName: string;
  targetAmount: number;
  actualAmount: number;
  completionRate: number;
  completedAt: number;
  duration: number; // in days
}

export interface UserStats {
  userId: string;
  lastUpdated: number;

  // Overall Performance
  totalExercises: number;
  totalSets: number;
  challengesCompleted: number;
  challengesJoined: number;
  currentStreak: number;
  longestStreak: number;
  lastActiveDate: string;

  // Exercise-Specific Stats
  exerciseStats: Record<string, ExerciseStat>;

  // Completed Challenges History
  completedChallenges: CompletedChallenge[];
}
